import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { fromKm, fmt, unitLabel } from "@/lib/units";
import { colorForVehicle } from "@/lib/vehicleColors";
import { useThemeColors } from "@/lib/theme";

function SingleTooltip({ active, payload, label, unit }) {
  if (!active || !payload?.length) return null;
  const actual = payload.find((p) => p.dataKey === "actual")?.value || 0;
  const forecast = payload.find((p) => p.dataKey === "forecast")?.value || 0;
  const total = actual + forecast;
  const isProjected = forecast > 0 && actual === 0;
  return (
    <div className="bg-[var(--bg-input)] border border-[var(--border-subtle)] px-3 py-2" style={{ borderRadius: 2 }}>
      <div className="label-tiny mb-1">{label} {isProjected && "· projected"}</div>
      <div className="font-mono-data text-sm text-[var(--text-primary)]">
        {fmt(total, 1)} <span className="text-[var(--text-muted)]">{unitLabel(unit)}</span>
      </div>
      {actual > 0 && forecast > 0 && (
        <div className="text-[10px] font-mono-data text-[var(--text-muted)] mt-0.5">
          {fmt(actual, 0)} actual + {fmt(forecast, 0)} forecast
        </div>
      )}
    </div>
  );
}

/**
 * Multi-vehicle tooltip — shows the month total followed by a per-vehicle
 * breakdown line for every active vehicle that contributed to the stack.
 */
function MultiTooltip({ active, payload, label, unit, vehicles }) {
  if (!active || !payload?.length) return null;
  // The payload is one entry per <Bar>. We have actual+forecast keys per
  // vehicle, so reduce the per-vehicle totals.
  const perVehicle = {};
  let total = 0;
  let anyForecast = false;
  let anyActual = false;
  for (const p of payload) {
    const key = p.dataKey || "";
    const m = key.match(/^(.+)_(actual|forecast)$/);
    if (!m) continue;
    const [, vid, kind] = m;
    const v = (p.value || 0);
    if (v <= 0) continue;
    if (kind === "actual") anyActual = true;
    if (kind === "forecast") anyForecast = true;
    perVehicle[vid] = (perVehicle[vid] || 0) + v;
    total += v;
  }
  const u = unitLabel(unit);
  const isProjected = !anyActual && anyForecast;
  return (
    <div className="bg-[var(--bg-input)] border border-[var(--border-subtle)] px-3 py-2 min-w-[180px]" style={{ borderRadius: 2 }}>
      <div className="label-tiny mb-1">
        {label} {isProjected && "· projected"}
      </div>
      <div className="font-mono-data text-sm text-[var(--text-primary)]">
        Total: {fmt(total, 0)} <span className="text-[var(--text-muted)]">{u}</span>
      </div>
      <div className="mt-1.5 space-y-0.5">
        {vehicles.map((v) => {
          const km = perVehicle[v.id] || 0;
          if (km <= 0) return null;
          const c = colorForVehicle(v.id, vehicles);
          return (
            <div key={v.id} className="flex items-center gap-1.5 text-[11px] font-mono-data">
              <span
                className="inline-block w-1.5 h-1.5"
                style={{ background: c.fill, borderRadius: 1 }}
              />
              <span className="text-[var(--text-secondary)] truncate max-w-[120px]">{v.name}:</span>
              <span className="text-[var(--text-primary)]">{fmt(km, 0)} {u}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function MonthlyChart({
  forecast,
  unit,
  yearUsedKm = 0,
  yearlyLimitKm = 0,
  isMultiAll = false,
  morphCapable = false,
  vehicles = [],
  perVehicleForecast = {},
}) {
  const multi = isMultiAll && vehicles.length > 1;
  const tc = useThemeColors();

  // Build per-vehicle indexed-by-month series for the multi-mode stack.
  // Aligns each vehicle's forecast row by `month` key against the aggregate
  // 12-month frame so the X-axis stays identical between modes.
  const data = multi
    ? (forecast || []).map((agg) => {
        const row = {
          label: agg.label,
          isCurrent: agg.isCurrent,
          isFuture: agg.isFuture,
        };
        for (const v of vehicles) {
          const series = perVehicleForecast[v.id] || [];
          const match = series.find((s) => s.label === agg.label);
          row[`${v.id}_actual`] = fromKm(match?.actual || 0, unit);
          row[`${v.id}_forecast`] = fromKm(match?.forecast || 0, unit);
        }
        return row;
      })
    : (forecast || []).map((m) => ({
        label: m.label,
        actual: fromKm(m.actual || 0, unit),
        forecast: fromKm(m.forecast || 0, unit),
        isCurrent: m.isCurrent,
        isFuture: m.isFuture,
      }));

  const ytdUsed = fromKm(yearUsedKm || 0, unit);
  const ytdLimit = fromKm(yearlyLimitKm || 0, unit);
  const ytdPct = ytdLimit > 0 ? Math.min(100, (ytdUsed / ytdLimit) * 100) : 0;

  // Pin a min-height when morph-capable so the card frame doesn't resize
  // when the user toggles between single and All views.
  const stableStyle = morphCapable ? { minHeight: 460 } : undefined;

  return (
    <div
      className="bg-[var(--bg-elevated)] border border-[var(--border-hover)] rounded-lg p-5 h-full flex flex-col card-hover"
      style={stableStyle}
      data-testid="monthly-chart"
      data-mode={multi ? "multi" : "single"}
    >
      <div className="flex items-start justify-between mb-2 gap-3 flex-wrap">
        <div>
          <h3 className="font-display text-lg font-medium text-[var(--text-primary)]">Monthly Mileage</h3>
          <p className="text-xs text-[var(--text-secondary)] mt-0.5">
            Distance driven by month · {unitLabel(unit)}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {ytdLimit > 0 && (
            <div
              className="flex items-center gap-2 px-2.5 py-1 bg-[var(--bg-input)] border border-[var(--border-subtle)]"
              style={{ borderRadius: 2 }}
              data-testid="monthly-chart-ytd"
            >
              <span className="label-tiny">YTD</span>
              <span className="font-mono-data text-xs text-[var(--text-primary)]">
                {fmt(ytdUsed, 0)} <span className="text-[var(--text-muted)]">/ {fmt(ytdLimit, 0)}</span>
              </span>
              <span className="font-mono-data text-[10px] text-[var(--text-secondary)]">{ytdPct.toFixed(0)}%</span>
            </div>
          )}
          {/* Legend — vehicle swatches in multi mode, actual/projected in single. */}
          <div className="flex items-center gap-3 text-[11px] tracking-wider uppercase shrink-0" data-testid="monthly-chart-legend">
            {multi ? (
              vehicles.map((v) => {
                const c = colorForVehicle(v.id, vehicles);
                return (
                  <span
                    key={v.id}
                    className="flex items-center gap-1.5 text-[var(--text-secondary)]"
                    data-testid={`monthly-chart-legend-${v.id}`}
                  >
                    <span
                      className="inline-block w-2 h-2"
                      style={{ background: c.fill, borderRadius: 1 }}
                    />
                    <span className="truncate max-w-[90px] normal-case tracking-tight text-[11px]">
                      {v.name}
                    </span>
                  </span>
                );
              })
            ) : (
              <>
                <span className="flex items-center gap-1.5 text-[var(--text-secondary)]">
                  <span className="w-2 h-2 bg-[var(--bg-inverse)] rounded-full" />
                  <span>Actual</span>
                </span>
                <span className="flex items-center gap-1.5 text-[var(--text-secondary)]">
                  <span
                    className="block w-3 h-2 border border-dashed border-[var(--border-strong)]"
                    style={{ background: "transparent" }}
                  />
                  <span>Projected</span>
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 min-h-[260px] mt-2">
        {data.length === 0 ? (
          <div className="h-full flex items-center justify-center text-[var(--text-muted)] text-sm">
            No trip data
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 16, right: 8, left: 4, bottom: 0 }}>
              <CartesianGrid stroke={tc["border-hover"]} vertical={false} />
              <XAxis
                dataKey="label"
                stroke={tc["text-muted"]}
                tick={(props) => {
                  const { x, y, payload, index } = props;
                  const isCurrent = data[index]?.isCurrent;
                  return (
                    <text
                      x={x}
                      y={y + 14}
                      textAnchor="middle"
                      fill={isCurrent ? tc["text-primary"] : tc["text-muted"]}
                      fontSize={11}
                      fontFamily="JetBrains Mono"
                      fontWeight={isCurrent ? 600 : 400}
                    >
                      {payload.value}
                    </text>
                  );
                }}
                axisLine={{ stroke: tc["border-subtle"] }}
                tickLine={false}
              />
              <YAxis
                width={40}
                stroke={tc["text-muted"]}
                tick={{ fill: tc["text-muted"], fontSize: 11, fontFamily: "JetBrains Mono" }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                cursor={{ fill: tc["bg-hover"] || tc["border-hover"] }}
                content={(props) =>
                  multi ? (
                    <MultiTooltip {...props} unit={unit} vehicles={vehicles} />
                  ) : (
                    <SingleTooltip {...props} unit={unit} />
                  )
                }
              />
              {multi ? (
                <>
                  {/* One actual bar per vehicle — stacked. Vehicle slot color. */}
                  {vehicles.map((v) => {
                    const c = colorForVehicle(v.id, vehicles);
                    return (
                      <Bar
                        key={`actual-${v.id}`}
                        dataKey={`${v.id}_actual`}
                        stackId="m"
                        fill={c.fill}
                        radius={[0, 0, 0, 0]}
                        barSize={18}
                        animationDuration={700}
                      />
                    );
                  })}
                  {/* One forecast bar per vehicle — same colors, lower opacity
                      with dashed stroke to keep the "projected" visual language. */}
                  {vehicles.map((v, idx) => {
                    const vc = colorForVehicle(v.id, vehicles);
                    const isLast = idx === vehicles.length - 1;
                    return (
                      <Bar
                        key={`forecast-${v.id}`}
                        dataKey={`${v.id}_forecast`}
                        stackId="m"
                        fill={vc.fill}
                        fillOpacity={0.22}
                        stroke={tc["border-strong"]}
                        strokeDasharray="3 3"
                        strokeWidth={1}
                        radius={isLast ? [3, 3, 0, 0] : [0, 0, 0, 0]}
                        barSize={18}
                        animationDuration={700}
                      />
                    );
                  })}
                </>
              ) : (
                <>
                  <Bar dataKey="actual" stackId="m" radius={[0, 0, 0, 0]} barSize={18} animationDuration={700}>
                    {data.map((entry, i) => (
                      <Cell key={`a-${i}`} fill={entry.isCurrent ? "#E82127" : tc["bg-inverse"]} />
                    ))}
                  </Bar>
                  <Bar
                    dataKey="forecast"
                    stackId="m"
                    radius={[3, 3, 0, 0]}
                    barSize={18}
                    fillOpacity={0.18}
                    stroke={tc["border-strong"]}
                    strokeDasharray="3 3"
                    strokeWidth={1}
                    animationDuration={700}
                  >
                    {data.map((entry, i) => (
                      <Cell key={`f-${i}`} fill={entry.isCurrent ? "#E82127" : tc["bg-inverse"]} />
                    ))}
                  </Bar>
                </>
              )}
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
